import { useEffect, useRef, useState } from "react";
import { TokensContext } from "./TokensContext";
import useAPI from "../../../hooks/useAPI";
import useSmartContracts from "../../../hooks/useSmartContracts";
import useCompany from "../../../hooks/useCompany";
import PropTypes from "prop-types";
import useNetworks from "../../../hooks/useNetworks";

const DEPLOY_POLL_INTERVAL_MS = 4000;
const DEPLOY_POLL_MAX_MS = 2 * 60 * 1000;

export default function TokensProvider({ children }) {
  const { activeCompany } = useCompany();
  const api = useAPI();
  const { networks, loadNetworks } = useNetworks();
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [companyContracts, setCompanyContracts] = useState({});
  const [abis, setAbis] = useState({});
  const [pendingDeployIds, setPendingDeployIds] = useState([]);
  const pollerRef = useRef(null);
  const smartContractsApi = useSmartContracts();

  useEffect(() => {
    loadNetworks()

    api.getAbis().then((res) => {
      setAbis(res || {});
    }).catch((err) => console.error(err));

    loadCompanySmartContracts();
  }, [activeCompany]);

  useEffect(() => {
    if (networks.length > 0 && !selectedNetwork) {
      setSelectedNetwork(networks[0]);
    }
  }, [networks]);

  useEffect(() => {
    if (!selectedNetwork) return;
    loadContracts();
  }, [selectedNetwork, activeCompany]);

  const loadContracts = async () => {
    if (!selectedNetwork) return;
    const allContracts = await api.getAdminSmartContracts();
    const filtered = allContracts.filter(c => c.networkId === selectedNetwork.id);
    setContracts(filtered);
  };

  const loadCompanySmartContracts = async () => {
    if (!activeCompany?.id) return;
    const smartContracts = await smartContractsApi.getCompanySmartContracts(activeCompany.id);
    setCompanyContracts(Object.fromEntries(smartContracts.map((c) => [c.contractId, c.order])));
  };

  const reloadAll = async () => {
    await Promise.all([loadContracts(), loadCompanySmartContracts()]);
  };

  const trackDeployment = (tokenId) => {
    if (!tokenId) return;
    setPendingDeployIds((prev) => (prev.includes(tokenId) ? prev : [...prev, tokenId]));
  };

  useEffect(() => {
    if (pendingDeployIds.length === 0) return;
    const startedAt = Date.now();
    pollerRef.current = setInterval(async () => {
      try {
        const allContracts = await api.getAdminSmartContracts();
        const stillPending = pendingDeployIds.filter((id) => {
          const c = allContracts.find((x) => x.id === id);
          return !c || !c.address || !c.abiURL;
        });
        const filtered = selectedNetwork
          ? allContracts.filter((c) => c.networkId === selectedNetwork.id)
          : allContracts;
        setContracts(filtered);
        if (stillPending.length !== pendingDeployIds.length) {
          setPendingDeployIds(stillPending);
        }
        if (stillPending.length === 0 || Date.now() - startedAt > DEPLOY_POLL_MAX_MS) {
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          if (stillPending.length > 0) setPendingDeployIds([]);
        }
      } catch (err) {
        console.error("deploy polling failed", err);
      }
    }, DEPLOY_POLL_INTERVAL_MS);

    return () => {
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
    };
  }, [pendingDeployIds, selectedNetwork]);

  return (
    <TokensContext.Provider value={{
      networks,
      selectedNetwork,
      setSelectedNetwork,
      contracts,
      setContracts,
      companyContracts,
      setCompanyContracts,
      abis,
      pendingDeployIds,
      trackDeployment,
      loadCompanySmartContracts,
      reloadAll
    }}>
      {children}
    </TokensContext.Provider>
  );
}

TokensProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
